import React from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Hint } from '@/components/atoms/hint'
import { InputField } from '@/components/molecules/input-field'
import { TableBody, TableHeader, TableRow } from '@/components/organisms/settings/sales-manager/table'
import type { RestaurantHighlightRules } from '@/models/restaurant-manager'
import { clampInteger } from '@/utils/number'

type Props = {
  value: RestaurantHighlightRules
  disabled?: boolean
  onChange: (field: keyof RestaurantHighlightRules, nextValue: number) => void
}

export const HighlightSettingPane: React.FC<Props> = ({ value, disabled, onChange }) => {
  const { t } = useTranslation()

  return (
    <section css={containerStyle}>
      <div css={headerSpacerStyle} />
      <TableHeader css={tableStyle}>
        <div className="label-col">{t('RestaurantManager.HighlightSettings')}</div>
        <div className="value-col" />
      </TableHeader>
      <TableBody>
        <TableRow css={tableStyle}>
          <div className="label-col">
            <div css={labelInnerStyle}>
              <span>{t('RestaurantManager.CheckinDays')}</span>
              <Hint customCss={hintStyle}>{t('RestaurantManager.CheckinDaysHelp')}</Hint>
            </div>
          </div>
          <div className="value-col">
            <div css={valueRowStyle}>
              <span>{t('RestaurantManager.CheckinDaysPrefix')}</span>
              <InputField
                type="text"
                min={0}
                max={14}
                fieldWidth="56px"
                width={56}
                value={value.checkinDays}
                marginBottom={0}
                customStyle={inputFieldStyle}
                customInputStyle={inputStyle}
                disabled={disabled}
                handleChangeData={event => onChange('checkinDays', clampInteger(event.target.value, 0, 14, value.checkinDays))}
              />
              <span>{t('RestaurantManager.CheckinDaysSuffix')}</span>
            </div>
          </div>
        </TableRow>
        <TableRow css={tableStyle}>
          <div className="label-col">
            <div css={labelInnerStyle}>
              <span>{t('RestaurantManager.UpdatedDays')}</span>
              <Hint customCss={hintStyle}>{t('RestaurantManager.UpdatedDaysHelp')}</Hint>
            </div>
          </div>
          <div className="value-col">
            <div css={valueRowStyle}>
              <InputField
                type="text"
                min={0}
                max={7}
                fieldWidth="56px"
                width={56}
                value={value.updatedDays}
                marginBottom={0}
                customStyle={inputFieldStyle}
                customInputStyle={inputStyle}
                disabled={disabled}
                handleChangeData={event => onChange('updatedDays', clampInteger(event.target.value, 0, 7, value.updatedDays))}
              />
              <span>{t('RestaurantManager.UpdatedDaysSuffix')}</span>
            </div>
          </div>
        </TableRow>
      </TableBody>
    </section>
  )
}

const containerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
})

const headerSpacerStyle = css({
  borderBottom: '1px solid #F2F2F2',
  flexShrink: 0,
  height: 75,
})

const tableStyle = css({
  '.label-col': {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    marginLeft: 32,
    minWidth: 0,
  },
  '.value-col': {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-end',
    marginLeft: 24,
    paddingRight: 32,
    whiteSpace: 'nowrap',
    width: 320,
  },
})

const labelInnerStyle = css({
  alignItems: 'center',
  display: 'flex',
  gap: 8,
})

const valueRowStyle = css({
  alignItems: 'center',
  color: '#676767',
  display: 'flex',
  fontSize: 14,
  fontWeight: 500,
  gap: 10,
  justifyContent: 'flex-end',
  whiteSpace: 'nowrap',
  width: '100%',
})

const inputFieldStyle = css({
  flexShrink: 0,
  width: 56,
})

const inputStyle = css({
  padding: '9px 0',
  textAlign: 'center',
})

const hintStyle = css({
  '.help-icon': { width: 18, height: 18 },
  '.help-popup': {
    color: '#676767',
    fontSize: 12,
    left: 23,
    top: 0,
    padding: 16,
    lineHeight: 1.5,
    fontWeight: 'bold',
    width: 250,
  },
})
